/**
 * Hooks de dados da tela Configuração → Times & Roteamento (Item 366 Fase 4).
 *
 * Consome o backend real (apps/engine/src/modules/team-configs + routing-agent):
 *   - GET/POST/PATCH/DELETE /v1/team-configs(/:id)(/members/:userId) · team-config-*
 *   - GET/POST/DELETE /v1/routing-rules · playbook-author
 *
 * Queries GATEADAS por capability no `enabled` (não só no render) — sem a cap o
 * fetch nem dispara (evita 403 vazando em toast; lição "gatear o fetch"). Mutações
 * de Team usam If-Match (ADR-071) com a `version` do GET; 409 propaga ao chamador.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type {
  AddMemberBody,
  CreateRoutingRuleBody,
  CreateTeamBody,
  RoutingRuleDto,
  TeamView,
  UpdateMemberBody,
  UpdateTeamBody,
} from './types';
import type { UseMutationResult } from '@tanstack/react-query';

const TEAMS_KEY = 'team-configs-list';
const RULES_KEY = 'routing-rules-list';
const STALE = 60_000;

export function useTeamsList(): {
  data: TeamView[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('team-config-read');
  const query = useQuery({
    queryKey: [TEAMS_KEY],
    queryFn: () => api.listTeams(true),
    enabled: canRead,
    staleTime: STALE,
    retry: 1,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

export function useCreateTeam(): UseMutationResult<TeamView, unknown, CreateTeamBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTeamBody) => api.createTeam(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useUpdateTeam(): UseMutationResult<
  TeamView,
  unknown,
  { id: string; expectedVersion: number; body: UpdateTeamBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedVersion, body }) => api.updateTeam(id, expectedVersion, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useDeleteTeam(): UseMutationResult<
  void,
  unknown,
  { id: string; expectedVersion: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedVersion }) => api.deleteTeam(id, expectedVersion),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useAddMember(): UseMutationResult<
  TeamView,
  unknown,
  { teamId: string; body: AddMemberBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, body }) => api.addTeamMember(teamId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useUpdateMember(): UseMutationResult<
  TeamView,
  unknown,
  { teamId: string; userId: string; body: UpdateMemberBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId, body }) => api.updateTeamMember(teamId, userId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useRemoveMember(): UseMutationResult<
  TeamView,
  unknown,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }) => api.removeTeamMember(teamId, userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [TEAMS_KEY] }),
  });
}

export function useRoutingRules(): {
  data: RoutingRuleDto[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  // Regras vivem no módulo routing-agent (gate playbook-author). Quem gerencia
  // times (gestor/admin) também tem playbook-author — gate a query nela.
  const canRead = useCapability('playbook-author');
  const query = useQuery({
    queryKey: [RULES_KEY],
    queryFn: () => api.listRoutingRules(),
    enabled: canRead,
    staleTime: STALE,
    retry: 1,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

export function useCreateRoutingRule(): UseMutationResult<
  RoutingRuleDto,
  unknown,
  CreateRoutingRuleBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoutingRuleBody) => api.createRoutingRule(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [RULES_KEY] }),
  });
}

export function useDeleteRoutingRule(): UseMutationResult<
  void,
  unknown,
  { id: string; expectedVersion: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedVersion }) => api.deleteRoutingRule(id, expectedVersion),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [RULES_KEY] }),
  });
}
